import { z } from 'zod';
import type { ChatTopicListProjection, ChatTopicProjection } from '@projoint/shared';

/**
 * The URL contract of Chat (`/my/messages`), one surface on desktop and iPhone:
 *
 *   ?dm=<uuid>                      a direct-message thread
 *   ?team=<slug>&topic=<uuid>       a channel
 *     &thread=<uuid>                …with that message's thread open
 *                                   (desktop: right rail; iPhone: thread screen)
 *   ?team=<slug>                    a team's chat: its channels ("forum") —
 *                                   what a team's Chat tab and
 *                                   `/teams/:slug/chat` open (desktop: its
 *                                   General opens beside the channel column)
 *     &scope=team                   legacy (the old team-filtered list); ignored
 *   ?requests=1                     message requests
 *
 * `dm` wins over `team`/`topic` when both arrive.
 */
export const messagesSearchSchema = z.object({
  dm: z.string().uuid().optional(),
  team: z.string().optional(),
  topic: z.string().uuid().optional(),
  thread: z.string().uuid().optional(),
  scope: z.enum(['team']).optional(),
  requests: z.string().optional(),
});
export type MessagesSearch = z.infer<typeof messagesSearchSchema>;

/** `/teams/:slug/chat` search (legacy deep links + the team Chat tab). */
export const teamChatSearchSchema = z.object({
  topic: z.string().uuid().optional(),
  thread: z.string().uuid().optional(),
  dm: z.string().uuid().optional(),
});
export type TeamChatSearch = z.infer<typeof teamChatSearchSchema>;

/**
 * Where `/teams/:slug/chat` goes: there is no second chat product, so the
 * team's chat is Chat filtered to the team; a channel / thread / direct
 * message link keeps pointing at the same conversation.
 */
export function teamChatRedirectSearch(teamSlug: string, s: TeamChatSearch): MessagesSearch {
  if (s.dm) return { dm: s.dm };
  if (s.topic) return { team: teamSlug, topic: s.topic, ...(s.thread ? { thread: s.thread } : {}) };
  return { team: teamSlug };
}

/** The team Chat is filtered to, if any. */
export function scopedTeamSlug(s: MessagesSearch): string | undefined {
  if (s.dm || !s.team) return undefined;
  if (s.scope === 'team' || !s.topic) return s.team;
  return undefined;
}

/** Search that opens a channel, keeping the team filter when it is that team's. */
export function channelSearch(teamSlug: string, topicId: string, scopeTeam?: string): MessagesSearch {
  return { team: teamSlug, topic: topicId, ...(scopeTeam === teamSlug ? { scope: 'team' as const } : {}) };
}

/** The channel a team-filtered Chat opens first: General, else its newest channel. */
export function firstTeamChannel(list: ChatTopicListProjection | undefined): string | null {
  if (!list) return null;
  return list.general?.id ?? list.projects[0]?.id ?? list.custom[0]?.id ?? null;
}

/** A channel of a team's list by id — archived ones too, so old links still open. */
export function findTopic(
  list: ChatTopicListProjection | undefined,
  topicId: string,
): ChatTopicProjection | null {
  if (!list) return null;
  if (list.general?.id === topicId) return list.general;
  return (
    list.projects.find((t) => t.id === topicId) ??
    list.custom.find((t) => t.id === topicId) ??
    list.archived.find((t) => t.id === topicId) ??
    null
  );
}
